/**
 * Operations on the document's own containers, rather than on one entity inside them.
 */

import { valueForDocument, type DocumentContainer } from './patch'
import type { AppDocument } from '../schema'

/**
 * Mark a linked identity trusted, or not.
 *
 * **The lazy `if (!d.trust) d.trust = {}` guard has to stay, and it is a known hazard.** `trust` and
 * `sharedEntries` are optional and the five-container document creation does not seed them, so every
 * existing document lacks both and removing the guard throws on a live document. But two devices
 * reaching this branch concurrently each set the root key to a new map, and the map merge rule
 * discards one — the whole trust map, in both merge directions (#367 F5).
 *
 * The fix is to seed all seven containers at creation, as the Modeling Data guide describes, which
 * needs a migration past `CURRENT_SCHEMA_VERSION`. Centralising the guard here is what makes that a
 * change to two functions rather than to two components (ADR 0012:178-182).
 */
export function setTrust(
  doc: AppDocument,
  userId: string,
  trusted: boolean,
): void {
  if (!doc.trust) {
    doc.trust = {}
  }

  doc.trust[userId] = {
    trusted,
    setAt: new Date().toISOString(),
  }
}

/**
 * Record that an entry has been shared with somebody, as a separate document.
 *
 * The recipient is appended to the existing list rather than replacing it — a whole-list assignment
 * here loses a recipient added on another device (#367 F7).
 *
 * `docId` names the shared document minted by the caller, which is the one id in the app that
 * `document/ids.ts` does not mint: it is an Automerge document url's subject, not an id of anything
 * inside this document.
 */
export function recordSharedEntry(
  doc: AppDocument,
  entryId: string,
  docId: string,
  recipientUserId: string,
): void {
  if (!doc.sharedEntries) {
    doc.sharedEntries = {}
  }

  const existing = doc.sharedEntries[entryId]

  if (existing) {
    if (!existing.recipientUserIds.includes(recipientUserId)) {
      existing.recipientUserIds.push(recipientUserId)
    }
    return
  }

  doc.sharedEntries[entryId] = {
    docId,
    createdAt: new Date().toISOString(),
    recipientUserIds: [recipientUserId],
  }
}

/**
 * Write entities into one of the document's containers, as a restore does.
 *
 * **A large restore is one change transaction, and that has a ceiling.** 50,000 entries in a single
 * transaction panics the WASM core with `capacity overflow`, which is not a catchable JS exception —
 * the `try`/`catch` around the caller would not contain it (#367 F23). The same entities written in
 * batches succeed, so the caller batches; this operation is one batch.
 */
export function importEntities(
  doc: AppDocument,
  container: DocumentContainer,
  entities: Record<string, unknown>,
): void {
  // `trust` and `sharedEntries` are optional, so a restore into a document that predates them has
  // to create the map. Same lazy-creation hazard as `setTrust`, and the same reason it stays.
  if (container === 'trust' && !doc.trust) doc.trust = {}
  if (container === 'sharedEntries' && !doc.sharedEntries)
    doc.sharedEntries = {}

  const target = doc[container] as Record<string, unknown>

  for (const [id, entity] of Object.entries(entities)) {
    target[id] = valueForDocument(entity)
  }
}
